<template>
  <div
    ref="$block"
    :class="{
      'osk-products-filters': true,
      'osk-products-filters_loading': isMounted && globalLoading,
      'osk-sticky-down': sticky,
      'osk-sticky-down_hide': hideSticky,
    }"
    :style="{ top: `${headerHeight}px` }"
  >
    <BusinessProductsFiltersButton
      class="osk-products-filters__allFilters"
      :selected-count="allSelectedFiltersCount"
      name="Фильтры и сортировка"
      icon="Filter"
      @click="handleFiltersClick"
    />

    <div class="osk-products-filters__hotFilters">
      <BusinessProductsFiltersButton
        v-for="(filter, index) in hotFilters"
        :key="index"
        class="osk-products-filters__hotFilter"
        :filter="filter"
        :loading="loading"
        @click="handleFiltersClick"
      />
    </div>

    <BusinessProductsFiltersDialog
      v-model="showFiltersDialog"
      :product-filters="productFilters"
      :presets="presets"
      :base-category="baseCategory"
      :filter-item="filterItem"
      :sorting="sorting"
      :page="page"
      @update-filters="handleFiltersUpdate"
    />
  </div>
</template>

<script lang="ts" setup>
import {
  isBooleanFilter,
  isPriceFilter,
} from '~/utils/filters';
import {
  ProductFilters, ProductFilter, ProductSorting,
  ProductFilterRequest, BooleanFilter,
} from '~/restAPI/data-contracts';
import type { TFiltersByCode, TFiltersPresets } from '~/types/filters';
import { compilePageParam } from '~/services/analytics/oskellyAnalytics';

import { AnalyticsEvents } from '~/types/analytics';

const { $addEvent } = useNuxtApp();

const props = defineProps<{
  productFilters?: ProductFilters
  presets?: TFiltersPresets
  baseCategory?: IBreadCrumbsItem
  loading?: boolean
  globalLoading?: boolean
  sorting?: ProductSorting['code']
  homeBannerId?: string
}>();

const emits = defineEmits<{
  (e: 'update:loading', value?: boolean): void,
  (e: 'updateFilters', value?: ProductFilterRequest, filters?: ProductFilters | null): void,
  (e: 'updateBooleanFilter', value: BooleanFilter): void,
}>();

const $block = ref<HTMLElement>();

const { isMounted } = useUiBreakpoints();
const route = useRoute();
const { sticky, hideSticky } = useAnimateSticky({ block: $block });
const { headerHeight } = useHeader();

const showFiltersDialog = ref(false);
const filterItem = ref();

const page = computed(() => (props.homeBannerId ? 'home_banner_filter' : 'catalog_filter'));

const filtersByCode = computed(() => {
  const res = props.productFilters?.filters.reduce((result, filter) => {
    // eslint-disable-next-line no-param-reassign
    result[filter.code] = filter;

    return result;
  }, {} as TFiltersByCode);

  return res ?? {};
});

const hotFilters = computed(() => props.productFilters?.hotFilters?.map((filter) => filtersByCode.value[filter]));

const allSelectedFiltersCount = computed(() => {
  const res = props.productFilters?.filters.reduce((result, filter) => {
    const isBoolean = isBooleanFilter(filter);
    const isPrice = isPriceFilter(filter);

    // eslint-disable-next-line no-param-reassign
    if (isPrice && (filter.selectedLower || filter.selectedUpper)) result += 1;
    // eslint-disable-next-line no-param-reassign
    else if (isBoolean && filter.value) result += 1;
    // eslint-disable-next-line no-param-reassign
    else if (!isBoolean && !isPrice && filter.selectedValues?.length) result += 1;

    return result;
  }, 0);

  return res;
});

function handleFiltersClick(filter?: ProductFilter) {
  if (isBooleanFilter(filter)) {
    emits('updateBooleanFilter', filter);
    emits('update:loading', true);
  } else {
    filterItem.value = filter;
    showFiltersDialog.value = true;
  }

  $addEvent(AnalyticsEvents.FiltersLinkClick, {
    action: 'Filter',
    id_item: filter ? filter.code : 'all',
    page: compilePageParam(route.name as string),
  });
}

function handleFiltersUpdate(filtersRequest?: ProductFilterRequest, allFilters?: ProductFilters | null) {
  emits('updateFilters', filtersRequest, allFilters);
}
</script>

<style scoped lang="scss">
@import "~/assets/scss/settings/index";

.osk-products-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  z-index: 8;
  background: #fff;
  padding: $indent-compact 0 $indent-medium;

  @include media-query(lg-and-up) {
    padding: 0;
    padding-bottom: 0;
    margin-bottom: 38px;
    display: flex;
    flex-wrap: nowrap;
    margin-top: 42px;

    &.osk-sticky-down {
      &:after {
        content: '';
        position: absolute;
        top: 100%;
        height: 8px;
        width: 100%;
        background: #fff;
      }
    }
  }

  &_loading {
    pointer-events: none;

    :deep(.osk-filter-button) {
      opacity: .2;
    }
  }

  &__allFilters {
    width: 100%;
    margin-bottom: $indent-medium;
    height: 40px;

    @include media-query(lg-and-up) {
      width: auto;
      height: 38px;
      margin-bottom: 0;
      margin-right: $indent-compact;
    }
  }

  &__hotFilter {
    @include media-query(lg-and-up) {
      height: 38px;
    }
  }

  &__hotFilters {
    display: flex;
    width: 100%;
    overflow-y: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}

.osk-breadcrumbs + .osk-products-filters {
  @include media-query(lg-and-up) {
    margin-top: 32px;
  }
}
</style>
